<template>
  <div class="asset-page-container">
    <h1>Asset Details</h1>
    <div class="filter-buttons">
      <label>
        <input type="radio" v-model="filter" value="all" @change="filterAssets" /> All
      </label>
      <label>
        <input type="radio" v-model="filter" value="inactive" @change="filterAssets" /> Inactive
      </label>
      <label>
        <input type="radio" v-model="filter" value="active" @change="filterAssets" /> Active
      </label>
    </div>
    <table>
      <thead>
        <tr>
          <th @click="sortAssets('devicename')">Device Name <span :class="getArrowClass('devicename')"></span></th>
          <th>Image</th>
          <th @click="sortAssets('tag_mac')">Tag ID <span :class="getArrowClass('tag_mac')"></span></th>
          <th @click="sortAssets('devicetype')">Device Type <span :class="getArrowClass('devicetype')"></span></th>
          <th @click="sortAssets('tag_temperature')">Temp <span :class="getArrowClass('tag_temperature')"></span></th>
          <th @click="sortAssets('tag_vol')">Battery <span :class="getArrowClass('tag_vol')"></span></th>
          <th @click="sortAssets('tag_rssi')">RSSI <span :class="getArrowClass('tag_rssi')"></span></th>
          <th @click="sortAssets('building')">Building <span :class="getArrowClass('building')"></span></th>
          <th @click="sortAssets('floor')">Floor <span :class="getArrowClass('floor')"></span></th>
          <th @click="sortAssets('taglocation')">Last Scanned Zone <span :class="getArrowClass('taglocation')"></span></th>
          <th @click="sortAssets('assignedzone')">Assigned Zone <span :class="getArrowClass('assignedzone')"></span></th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="filteredAssets.length === 0">
          <td colspan="12">No assets found matching your search.</td>
        </tr>
        <tr v-else v-for="asset in filteredAssets" :key="asset.tag_mac">
          <td>{{ asset.devicename }}</td>
          <td>
            <template v-if="asset.imagepath">
              <img :src="getAssetImage(asset.imagepath)" alt="Asset Image" class="table-image" />
            </template>
            <template v-else>
              <span class="material-symbols-outlined asset-icon">
                {{ getAssetIcon(asset) }}
              </span>
            </template>
          </td>
          <td>{{ asset.tag_mac }}</td>
          <td>{{ asset.devicetype }}</td>
          <td>
            <span class="material-symbols-outlined thermostat" style="font-size: 16px;">device_thermostat</span>
            <span>{{ asset.tag_temperature }} °C</span>
          </td>
          <td>
            <span :class="getBatteryClass(asset.tag_vol)">
              {{ asset.tag_vol >= 2.7 ? 'Good' : asset.tag_vol >= 2.3 ? 'Mid' : 'None' }}
            </span>
            <span class="battery-value"> ({{ asset.tag_vol.toFixed(1) }}v)</span>
          </td>
          <td>
            <span v-if="Math.abs(asset.tag_rssi) <= 70">
              <span class="material-symbols-outlined rssi-good" style="font-size: 24px;">signal_cellular_alt</span>
            </span>
            <span v-else>
              <span class="material-symbols-outlined rssi-mid" style="font-size: 24px;">signal_cellular_alt_2_bar</span>
            </span>
          </td>
          <td>{{ asset.building }}</td>
          <td>{{ asset.floor }}</td>
          <td>{{ asset.taglocation }}</td>
          <td>{{ asset.assignedzone }}</td>
          <td>
            <button @click="openEditSidebar(asset)">EDIT</button>
          </td>
        </tr>
      </tbody>
    </table>

    <EditAssetPage
      v-if="selectedAsset" 
      :visible="isEditSidebarVisible"
      :asset="selectedAsset"
      @save="updateAsset"
      @close="closeEditSidebar"
    />
  </div>
</template>

<script>
import axios from 'axios';
import EditAssetPage from './EditAssetPage.vue';

export default {
  components: {
    EditAssetPage,
  },
  data() {
    return {
      filter: 'all',
      assets: [],
      filteredAssets: [],
      sortKey: 'devicename',
      sortOrder: 'ASC',
      isEditSidebarVisible: false,
      selectedAsset: null,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      autoRefreshInterval: null,
    };
  },
  created() {
    const filterFromRoute = this.$route.query.filter || 'all';
    this.filter = filterFromRoute;
    this.loadAssets();
    this.checkActiveStatus();
    this.autoRefreshInterval = setInterval(this.loadAssets, 5000);
  },
  beforeUnmount() {
    clearInterval(this.autoRefreshInterval);
  },
  watch: {
    '$route'(to, from) {
      // 라우터 쿼리 파라미터가 변경될 때 필터 업데이트
      if (to.query.filter !== from.query.filter) {
        this.filter = to.query.filter || 'all';
        this.currentPage = 1; // 페이지를 첫 번째로 리셋
        this.loadAssets();
      }
    }
  },
  methods: {
    async loadAssets() {
      try {
        const company = localStorage.getItem('company');
        const response = await axios.get('/api/assets', {
          params: {
            company: company,
            limit: this.itemsPerPage,
            offset: (this.currentPage - 1) * this.itemsPerPage,
            sortKey: this.sortKey,
            sortOrder: this.sortOrder,
            filter: this.filter
          }
        });

        // 응답 데이터 구조 확인 및 처리
        if (response.data && response.data.data) {
          // 데이터 확인을 위한 로그
          console.log('Loaded assets with icons:', response.data.data);
          
          this.assets = response.data.data;
          this.totalItems = response.data.total;
          this.filteredAssets = this.assets;
        } else if (Array.isArray(response.data)) {
          console.log('Loaded assets (array):', response.data);
          
          this.assets = response.data;
          this.totalItems = response.data.length;
          this.filteredAssets = this.assets;
        } else {
          console.error('Invalid response format:', response.data);
          this.assets = [];
          this.totalItems = 0;
          this.filteredAssets = [];
        }
        
        // 데이터 로드 후 필터링 적용
        this.filterAssets();
      } catch (error) {
        console.error('자산을 불러오지 못했습니다:', error);
        this.assets = [];
        this.totalItems = 0;
        this.filteredAssets = [];
      }
    },
    updateIntervalChange() {
    console.log(`Update interval changed to: ${this.updateInterval}`);
    if (this.updateInterval < 5) {
      this.updateInterval = 5;
      alert('최소 업데이트 간격은 5초입니다.');
    }
      this.startUpdateTimer();
    },
    
    filterAssets() {
      // assets가 배열인지 확인
      if (!Array.isArray(this.assets)) {
        console.error('assets is not an array:', this.assets);
        this.filteredAssets = [];
        return;
      }

      // 필터링 로직
      this.filteredAssets = this.assets.filter(asset => {
        if (this.filter === 'inactive') {
          return asset.active === -1;
        } else if (this.filter === 'active') {
          return asset.active === 0;
        }
        return true; // 'all' 필터일 경우
      });
    },
    sortAssets(key, keepOrder = false) {
      if (!keepOrder) {
        this.sortOrder = this.sortKey === key ? -this.sortOrder : 1;
      }
      this.sortKey = key;
      this.filteredAssets.sort((a, b) => {
        if (a[key] < b[key]) return -1 * this.sortOrder;
        if (a[key] > b[key]) return 1 * this.sortOrder;
        return 0;
      });
    },
    checkActiveStatus() {
      axios.post('/api/checkactive/tag').then((response) => {
        console.log('Active 상태가 업데이트되었습니다:', response.data.message);
      }).catch(error => {
        console.error('Failed to check active status:', error);
      });
    },
    getArrowClass(key) {
      return this.sortKey === key ? (this.sortOrder === 1 ? 'arrow-up' : 'arrow-down') : 'arrow-default';
    },
    getAssetImage(imagePath) {
      try {
        return require(`@/assets/${imagePath}`);
      } catch (error) {
        console.error('이미지를 찾을 수 없습니다:', error);
        return null;  // 이미지를 찾을 수 없을 때 null 반환
      }
    },
    getBatteryClass(vol) {
      return vol >= 2.7 ? 'battery_good' : vol >= 2.3 ? 'battery_mid' : 'battery_none';
    },
    openEditSidebar(asset) {
      this.selectedAsset = asset;
      this.isEditSidebarVisible = true;
    },
    closeEditSidebar() {
      this.isEditSidebarVisible = false;
      this.selectedAsset = null;
    },
    updateAsset(formData) {
      try {
        // FormData 객체인지 확인
        if (!(formData instanceof FormData)) {
          console.error('Invalid formData format:', formData);
          return;
        }

        // company 값이 이미 있는지 확인
        if (!formData.has('company')) {
          formData.append('company', localStorage.getItem('company'));
        }

        // 디버깅을 위한 FormData 내용 출력
        console.log('Sending FormData:');
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }

        axios.post('/api/updateAssetDetails', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(response => {
          console.log('업데이트 성공:', response.data);
          this.closeEditSidebar();
          this.loadAssets();
        })
        .catch(error => {
          console.error('업데이트 실패:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
          }
        });
      } catch (error) {
        console.error('FormData 처리 중 오류:', error);
      }
    },
    getAssetIcon(asset) {
      // 기본 아이콘 설정
      const defaultIcon = 'inventory_2';
      
      // asset.icon이 있으면 해당 아이콘을, 없으면 기본 아이콘을 반환
      return asset.icon || defaultIcon;
    },
  },
};
</script>

<style scoped>
.asset-page-container {
  padding: 10px;
  max-width: 1680px;
  margin: 0 auto;
  background-color: #f5f5f5;
  min-height: 100vh;
}

/* 필터 버튼 섹션 */
.filter-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  padding: 20px 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.filter-buttons label {
  display: flex;
  align-items: center;
  margin: 0 15px;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.filter-buttons label:hover {
  background-color: #f8f9fa;
}

.filter-buttons input[type="radio"] {
  margin-right: 8px;
  accent-color: #4e73df;
}

/* 테이블 컨테이너 */
table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 14px;
}

/* 테이블 헤더 */
th {
  background-color: #f8f9fa;
  padding: 16px 12px;
  text-align: left;
  cursor: pointer;
  position: relative;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
  user-select: none;
  transition: background-color 0.2s;
}

th:hover {
  background-color: #e9ecef;
}

/* 테이블 바디 */
td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  color: #333;
}

tbody tr:hover {
  background-color: #f8f9fa;
}

tbody tr:last-child td {
  border-bottom: none;
}

/* 정렬 화살표 */
th .arrow-default::after {
  content: '↕';
  color: #ccc;
  margin-left: 5px;
}

th .arrow-up::after {
  content: '↑';
  color: #4e73df;
  margin-left: 5px;
}

th .arrow-down::after {
  content: '▼';
  color: #4e73df;
  margin-left: 5px;
}

/* 이미지 및 아이콘 */
table img,
.table-image {
  width: 40px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
}

.asset-icon {
  font-size: 40px !important;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  transition: color 0.2s;
}

.asset-icon:hover {
  color: #4e73df;
}

/* 배터리 상태 */
.battery_good {
  color: #1cc88a !important;
  font-weight: 600;
}

.battery_mid {
  color: #f6c23e !important;
  font-weight: 600;
}

.battery_none {
  color: #36b9cc !important;
  font-weight: 600;
}

.battery-value {
  color: #333;
  font-size: 12px;
}

/* RSSI 신호 강도 */
.rssi-good {
  color: #1cc88a;
}

.rssi-mid {
  color: #f6c23e;
}

.rssi-low {
  color: #e74a3b;
}

/* 온도계 아이콘 */
.thermostat {
  color: #e74a3b;
}

/* 버튼 스타일 */
button {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  background-color: #4e73df;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

button:hover {
  background-color: #3a5bc7;
  transform: translateY(-1px);
}

/* 반응형 디자인 - 태블릿 (1200px 이하) */
@media screen and (max-width: 1200px) {
  .asset-page-container {
    padding: 8px;
    max-width: 100%;
  }
  
  table {
    font-size: 13px;
  }
  
  th {
    padding: 12px 8px;
  }
  
  td {
    padding: 10px 8px;
  }
  
  .filter-buttons {
    padding: 15px 0;
    margin-bottom: 15px;
  }
  
  .filter-buttons label {
    margin: 0 10px;
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .asset-icon {
    font-size: 35px !important;
    height: 50px;
  }
  
  table img,
  .table-image {
    width: 35px;
    height: 50px;
  }
}

/* 반응형 디자인 - 모바일 (768px 이하) */
@media screen and (max-width: 768px) {
  .asset-page-container {
    padding: 5px;
  }
  
  table {
    font-size: 12px;
    min-width: 800px; /* 가로 스크롤 허용 */
  }
  
  th {
    padding: 10px 6px;
    font-size: 11px;
  }
  
  td {
    padding: 8px 6px;
  }
  
  .filter-buttons {
    padding: 12px 0;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filter-buttons label {
    margin: 0 5px;
    padding: 5px 10px;
    font-size: 12px;
  }
  
  .asset-icon {
    font-size: 30px !important;
    height: 45px;
  }
  
  table img,
  .table-image {
    width: 30px;
    height: 45px;
  }
  
  button {
    padding: 4px 8px;
    font-size: 11px;
  }
  
  /* 모바일에서 일부 컬럼 숨기기 */
  table th:nth-child(4),
  table td:nth-child(4),
  table th:nth-child(8),
  table td:nth-child(8),
  table th:nth-child(9),
  table td:nth-child(9) {
    display: none;
  }
}

/* 반응형 디자인 - 작은 모바일 (480px 이하) */
@media screen and (max-width: 480px) {
  .asset-page-container {
    padding: 3px;
  }
  
  table {
    font-size: 11px;
    min-width: 600px;
  }
  
  th {
    padding: 8px 4px;
    font-size: 10px;
  }
  
  td {
    padding: 6px 4px;
  }
  
  .filter-buttons {
    padding: 10px 0;
    margin-bottom: 10px;
  }
  
  .filter-buttons label {
    margin: 2px;
    padding: 4px 8px;
    font-size: 11px;
  }
  
  .asset-icon {
    font-size: 25px !important;
    height: 40px;
  }
  
  table img,
  .table-image {
    width: 25px;
    height: 40px;
  }
  
  button {
    padding: 3px 6px;
    font-size: 10px;
  }
  
  /* 작은 모바일에서 더 많은 컬럼 숨기기 */
  table th:nth-child(3),
  table td:nth-child(3),
  table th:nth-child(6),
  table td:nth-child(6),
  table th:nth-child(7),
  table td:nth-child(7),
  table th:nth-child(10),
  table td:nth-child(10),
  table th:nth-child(11),
  table td:nth-child(11) {
    display: none;
  }
}

/* 큰 화면에서의 최적화 (1600px 이상) */
@media screen and (min-width: 1600px) {
  .asset-page-container {
    padding: 15px;
  }
  
  table {
    font-size: 15px;
  }
  
  th {
    padding: 18px 16px;
  }
  
  td {
    padding: 14px 16px;
  }
  
  .filter-buttons {
    padding: 25px 0;
    margin-bottom: 25px;
  }
  
  .filter-buttons label {
    margin: 0 20px;
    padding: 10px 20px;
    font-size: 15px;
  }
  
  .asset-icon {
    font-size: 45px !important;
    height: 65px;
  }
  
  table img,
  .table-image {
    width: 45px;
    height: 65px;
  }
  
  button {
    padding: 8px 16px;
    font-size: 13px;
  }
}

/* 테이블 컨테이너 오버플로우 처리 */
@media screen and (max-width: 768px) {
  .asset-page-container {
    overflow-x: auto;
  }
  
  table {
    margin-bottom: 20px;
  }
}

/* 접근성 개선 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* 다크모드 대응 (선택사항) */
@media (prefers-color-scheme: dark) {
  .asset-page-container {
    background-color: #1a1a1a;
    color: #ffffff;
  }
  
  table,
  .filter-buttons {
    background: #2d2d2d;
    color: #ffffff;
  }
  
  th {
    background-color: #3d3d3d;
    color: #ffffff;
  }
  
  td {
    border-bottom-color: #404040;
  }
  
  tbody tr:hover {
    background-color: #404040;
  }
}
</style>
